// src/components/EmailVerificationNotice.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { auth } from '../firebaseConfig';
import { onIdTokenChanged, sendEmailVerification } from 'firebase/auth';

const COOLDOWN_SECONDS = 60;
const POLL_MS = 10_000;
const MAX_POLLS = 12; // ~2 minutes

/**
 * Global email verification banner.
 * Shows when a user is signed in but emailVerified === false.
 *
 * Mount it once, just under your fixed top bar (e.g., in DashboardLayout).
 * If your top bar height differs, pass topOffset (e.g., "64px").
 */
export default function EmailVerificationNotice({ topOffset = '56px' }) {
  const [visible, setVisible] = useState(
    !!auth.currentUser && auth.currentUser.emailVerified === false
  );
  const [cooldownLeft, setCooldownLeft] = useState(0);

  const pollCount = useRef(0);
  const pollRef = useRef(null);
  const cooldownRef = useRef(null);

  // Colors
  const bg = useColorModeValue('var(--chakra-colors-yellow-100)', 'rgba(254, 243, 199, 0.12)');
  const border = useColorModeValue('var(--chakra-colors-yellow-300)', 'rgba(252, 211, 77, 0.36)');
  const text = useColorModeValue('var(--chakra-colors-yellow-800)', 'var(--chakra-colors-yellow-200)');

  // Watch auth/claims changes
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, (u) => {
      const next = !!u && u.emailVerified === false;
      setVisible(next);
      if (!!u && u.emailVerified) {
        stopPolling();
      }
    });
    return () => unsub();
  }, []);

  // Refresh on window focus (helps catch verification after user clicks link)
  useEffect(() => {
    const onFocus = async () => {
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        await safeReload();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    cooldownRef.current = setInterval(() => {
      setCooldownLeft((n) => {
        if (n <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldownLeft]);

  const safeReload = async () => {
    try {
      await auth.currentUser?.reload();
      if (auth.currentUser?.emailVerified) {
        setVisible(false);
      }
    } catch {
      // ignore
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      await safeReload();
      if (!auth.currentUser || auth.currentUser.emailVerified || pollCount.current >= MAX_POLLS) {
        stopPolling();
      }
    }, POLL_MS);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const sendVerification = async () => {
    if (!auth.currentUser || cooldownLeft > 0) return;
    try {
      await sendEmailVerification(auth.currentUser);
      setCooldownLeft(COOLDOWN_SECONDS);
      startPolling();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('sendEmailVerification failed:', e);
    }
  };

  if (!visible) return null;

  return (
    <Box
      position="fixed"
      top={topOffset}
      left={0}
      right={0}
      zIndex={200}
      bg={bg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Flex maxW="100%" px={4} py={2} align="center" justify="space-between" gap={4}>
        <Text fontSize="sm" color={text}>
          Please verify your email to secure your account. We’ll send a link to your inbox.
        </Text>
        <Button
          size="sm"
          onClick={sendVerification}
          isDisabled={cooldownLeft > 0}
          bg={cooldownLeft > 0 ? 'gray.200' : 'blue.600'}
          color={cooldownLeft > 0 ? 'gray.600' : 'white'}
          _hover={cooldownLeft > 0 ? {} : { bg: 'blue.700' }}
          _active={cooldownLeft > 0 ? {} : { bg: 'blue.800' }}
        >
          {cooldownLeft > 0 ? `Send again in ${cooldownLeft}s` : 'Send verification'}
        </Button>
      </Flex>
    </Box>
  );
}
